import { useState } from 'react';
import { MicePremisesRequestSchema, type MiceProjectView } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Card, Input, Textarea, useSnackbar } from '@web/ui/core';
import { PremisesCheck, PremisesField } from './premises_field';
import { useMicePremises } from '../hooks/use_mice_premises';
import { PremisesDestinationRow } from './premises_destination_row';
import { PremisesLimitsFields } from './premises_limits_fields';
import { PremisesReturnFields } from './premises_return_fields';
import { PremisesWarnings } from './premises_warnings';
import {
  emptyDestination,
  toForm,
  toPremises,
  type DestinationForm,
  type PremisesForm,
} from './premises_form';

interface PremisesPanelProps {
  projectId: string;
  project: MiceProjectView;
  canManage: boolean;
}

/** O que o salvar grava, para saber se o formulário mudou desde o que veio do servidor. */
const snapshot = (eventDate: string, text: string, form: PremisesForm): string =>
  JSON.stringify([eventDate, text, toPremises(form)]);

/** A conferência vale para o formulário em que foi feita: a gravada, enquanto nada muda (salvar
 *  de novo sem alterar mantém), ou a do checkbox; qualquer edição depois pede conferir de novo. */
function useConfirmation(wasChecked: boolean, current: string): [boolean, (next: boolean) => void] {
  const [saved] = useState(current);
  const [touched, setTouched] = useState<{ checked: boolean; at: string } | null>(null);
  const checked = touched?.at === current ? touched.checked : wasChecked && current === saved;
  return [
    checked,
    (next) => {
      setTouched({ checked: next, at: current });
    },
  ];
}

function PremisesPanelBody({
  projectId,
  project,
  canManage,
}: Readonly<PremisesPanelProps>): React.JSX.Element {
  const { extract, save } = useMicePremises(projectId);
  const { showSnackbar } = useSnackbar();
  const [eventDate, setEventDate] = useState(project.eventDate ?? '');
  const [text, setText] = useState(project.premisesText ?? '');
  const [form, setForm] = useState<PremisesForm>(() => toForm(project.premises));
  const wasChecked = project.premisesCheckedAt !== null;
  const [checked, setChecked] = useConfirmation(wasChecked, snapshot(eventDate, text, form));
  const patch = (next: Partial<PremisesForm>): void => {
    setForm((prev) => ({ ...prev, ...next }));
  };
  const patchDestination = (index: number, next: Partial<DestinationForm>): void => {
    patch({
      destinations: form.destinations.map((d, i) => (i === index ? { ...d, ...next } : d)),
    });
  };

  const onExtract = (): void => {
    extract.mutate(text, {
      onSuccess: (premises) => {
        setForm(toForm(premises));
      },
    });
  };

  const onSave = (): void => {
    const parsed = MicePremisesRequestSchema.safeParse({
      premisesText: text,
      eventDate,
      premises: toPremises(form),
      checked,
    });
    if (!parsed.success) {
      showSnackbar({ severity: 'error', message: t('mice.premises.invalid') });
      return;
    }
    save.mutate(parsed.data);
  };

  return (
    <Card>
      <div className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-meta font-medium text-foreground">{t('mice.premises.title')}</h2>
          {project.premisesCheckedAt === null ? (
            <span className="text-meta text-tone-amber-ink">{t('mice.premises.unchecked')}</span>
          ) : null}
        </div>
        <PremisesWarnings form={form} eventDate={eventDate} />
        <PremisesField id="premises-event-date" label={t('mice.premises.eventDate')}>
          <Input
            id="premises-event-date"
            type="date"
            value={eventDate}
            disabled={!canManage}
            onChange={(e) => {
              setEventDate(e.target.value);
            }}
          />
        </PremisesField>
        <PremisesField id="premises-text" label={t('mice.premises.text')}>
          <Textarea
            id="premises-text"
            value={text}
            disabled={!canManage}
            placeholder={t('mice.premises.textHint')}
            onChange={(e) => {
              setText(e.target.value);
            }}
          />
        </PremisesField>
        {canManage ? (
          <div>
            <Button
              size="sm"
              variant="outline"
              disabled={extract.isPending || text.trim() === ''}
              onClick={onExtract}
            >
              {t('mice.premises.extract')}
            </Button>
          </div>
        ) : null}
        <h3 className="text-meta font-medium text-foreground">{t('mice.premises.destinations')}</h3>
        {form.destinations.map((d, i) => (
          <PremisesDestinationRow
            key={d.key}
            index={i}
            value={d}
            canManage={canManage}
            canRemove={form.destinations.length > 1}
            onChange={(next) => {
              patchDestination(i, next);
            }}
            onRemove={() => {
              patch({ destinations: form.destinations.filter((_, j) => j !== i) });
            }}
          />
        ))}
        {canManage ? (
          <div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                patch({ destinations: [...form.destinations, emptyDestination()] });
              }}
            >
              {t('mice.premises.addDestination')}
            </Button>
          </div>
        ) : null}
        <PremisesReturnFields form={form} canManage={canManage} onChange={patch} />
        <PremisesLimitsFields form={form} canManage={canManage} onChange={patch} />
        <PremisesField id="premises-notes" label={t('mice.premises.notes')}>
          <Textarea
            id="premises-notes"
            value={form.notes}
            disabled={!canManage}
            onChange={(e) => {
              patch({ notes: e.target.value });
            }}
          />
        </PremisesField>
        {canManage ? (
          <PremisesCheck
            label={t('mice.premises.confirm')}
            checked={checked}
            disabled={false}
            onChange={setChecked}
          />
        ) : null}
        {canManage && wasChecked && !checked ? (
          <p className="text-meta text-tone-amber-ink">{t('mice.premises.uncheckOnSave')}</p>
        ) : null}
        {canManage ? (
          <div>
            <Button size="sm" disabled={save.isPending} onClick={onSave}>
              {t('mice.premises.save')}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** Recomeça o formulário quando o servidor devolve outras premissas (ex.: após salvar). */
export function PremisesPanel(props: Readonly<PremisesPanelProps>): React.JSX.Element {
  return (
    <PremisesPanelBody
      key={JSON.stringify([
        props.project.eventDate,
        props.project.premises,
        props.project.premisesCheckedAt,
      ])}
      {...props}
    />
  );
}
